/* eslint-disable prefer-arrow/prefer-arrow-functions */
import {environment} from '@env/environment';
import {AccountRole} from '@enums/AccountRole';
import {HotelStatus} from '@enums/HotelStatus';
import {AccountStatus} from '@enums/AccountStatus';
import {ProviderStatus} from '@enums/ProviderStatus';
import {CiecStatus} from '@enums/CiecStatus';
import * as pdfjsLib from 'pdfjs-dist';
import JSZip from 'jszip';
import { HttpErrorResponse } from '@angular/common/http';
import {ApiResponse, ErrorApiResponse} from '@models/api.response';

/**
 * Creator: Fernando Tapia
 * Company: TAXSAT
 * Date: 28.01.22
 *
 * Copyright TAXSAT
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function buildPath(pathComponents: string[]) {
    return pathComponents.reduce((base, next) => base + '/' + next, environment.backendUrl);
}

/**
 * Normaliza un texto a un slug seguro para nombre de archivo: minúsculas, sin acentos, sin
 * espacios ni caracteres especiales (solo `[a-z0-9-]`).
 *
 * @param text texto a normalizar (nombre de compañía, proveedor, etc.)
 * @param fallback valor a devolver si `text` viene vacío/nulo
 */
export function slugify(text: string, fallback: string = 'sin-nombre'): string {
    return (text ?? '').trim().toLowerCase()
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || fallback;
}

/**
 * Sanitiza un RFC para usarlo en un nombre de archivo/carpeta — solo `[A-Za-z0-9]`, conserva
 * mayúsculas (a diferencia de {@link slugify}) para verse igual que la carpeta interna que arma el
 * backend (`sanitizeRfcParaCarpeta` en `FirstUploadController`).
 *
 * @param rfc RFC a sanitizar
 * @param fallback valor a devolver si `rfc` viene vacío/nulo
 */
export function sanitizeRfc(rfc: string, fallback: string = 'SIN-RFC'): string {
    return (rfc ?? '').trim().replace(/[^A-Za-z0-9]/g, '') || fallback;
}

/**
 * Function to get img role
 *
 * @param role
 */
export function imgByRole(role: number): string {
    let imgRole = '';
    switch (role) {
        case AccountRole.master:
        case AccountRole.semiMaster:
            imgRole = 'https://sise-util.s3.amazonaws.com/role/master.png';
            break;
        case AccountRole.adminHotel:
            imgRole = 'https://sise-util.s3.amazonaws.com/role/hotel.png';
            break;
        case AccountRole.accountant:
            imgRole = 'https://sise-util.s3.amazonaws.com/role/trabajador.png';
            break;
        case AccountRole.provider:
            imgRole = 'https://sise-util.s3.amazonaws.com/role/proveedor.png';
            break;
    }
    return imgRole;
}

/**
 *
 */
export function statusEntity(statusData: number): string {
    let statusName = '';
    switch (statusData) {
        case HotelStatus.CREATED:
            statusName = 'Creado';
            break;
        case HotelStatus.ACTIVE:
            statusName = 'Activo';
            break;
        case HotelStatus.INACTIVE:
            statusName = 'Inactivo';
            break;
    }
    return statusName;
}

export function getRole(role: number) {
    switch (role) {
        case AccountRole.master:
            return 'Master';
            break;
        case AccountRole.adminHotel:
            return 'Contralor';
            break;
        case AccountRole.accountant:
            return 'Contador';
            break;
        case AccountRole.provider:
            return 'Proveedor';
            break;
    }
}

export function getStatus(status: number) {
    switch (status) {
        case AccountStatus.ACTIVE:
            return 'Activo';
            break;
        case AccountStatus.INACTIVE:
            return 'Inactivo';
            break;
    }
}

export function getStatusCIEC(status: number) {
    switch (status) {
        case CiecStatus.PENDING:
            return 'Pendiente';
            break;
        case CiecStatus.VALID:
            return 'Valida';
            break;
        case CiecStatus.REJECTED:
            return 'Rechazada';
            break;
        case CiecStatus.ERROR:
            return 'Error';
            break;
    }
}


export function getStatusProvider(status: number) {
    switch (status) {
        case ProviderStatus.APPROVED:
            return 'Aprobado';
            break;
        case ProviderStatus.INACTIVE:
            return 'Inactivo';
            break;
        case ProviderStatus.FINISHED:
            return 'Fin de servicio';
            break;
    }
}

export function getStatusEDOFAC(status: number) {
    switch (status) {
        case 1:
            return 'Saludable';
            break;
        case 2:
            return 'Sentencia Favorable';
            break;
        case 3:
            return 'Desvirtuado';
            break;
        case 4:
            return 'Definitivo';
            break;
        case 5:
            return 'Presunto';
        case 6:
            return 'Saludable';
        case 7:
            return 'Estrados';
            break;
    }
}

/**
 * Method to convert date string to back SISE
 * @param date
 */
export function toStringBackDate(dateString: string) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    if (month < 10) {
        return `${day}-0${month}-${year}`;
    } else {
        return `${day}-${month}-${year}`;
    }
}

/**
 * Method to get current Period
 */
export function currentPeriod() {
    const date = new Date();
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    if (month < 10) {
        return `0${month}-${year}`;
    } else {
        return `${month}-${year}`;
    }
}

/**
 * Method to get current Period Minus 1 Month
 */
export function currentPeriodBefore() {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    if (month < 10) {
        return `0${month}-${year}`;
    } else {
        return `${month}-${year}`;
    }
}

/**
 * Method to get current Period Minus 1 Month
 */
export function currentPeriodMatrix(dateInit, i) {
    const date = new Date(dateInit);
    date.setMonth(date.getMonth() + i);
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    if (month < 10) {
        return `0${month}-${year}`;
    } else {
        return `${month}-${year}`;
    }
}

/**
 *
 * @param dateInit
 * @param i
 * @param check
 */
export function currentPeriodMatrixCheck(dateInit, i, check) {
    const date = new Date(dateInit);
    date.setMonth(date.getMonth() + i);
    const month = date.getMonth() + 1;
    const year = date.getFullYear().toString().slice(-2);
    let dataDate = '';
    if (month < 10) {
        dataDate = `0${month}-${year}`;
    } else {
        dataDate = `${month}-${year}`;
    }
    return check === dataDate ? true : false;
}

/**
 *
 * @param dateFrom
 * @param dateTo
 */
export function monthDiff(dateFrom, dateTo) {
    return dateTo.getMonth() - dateFrom.getMonth() + (12 * (dateTo.getFullYear() - dateFrom.getFullYear()));
}

/**
 *
 * @param content
 */
export function dataSUA(content: string | ArrayBuffer): { rp: string; rfc: string; period: string } {
    let rfc = '';
    let period = '';
    let rp = '';
    for (const line of content.toString().split('                    ')) {
        rp = line.slice(2, 13);

        if (line.slice(13, 14) === ' ') rfc = line.slice(14, 26);
        else rfc = line.slice(13, 26);

        period = line.slice(30, 32) + '-' + line.slice(28, 30);
        break;
    }
    return {rp: rp, rfc: rfc, period: period};
}

/**
 *
 * @param file
 */
export function readFileAsync(file: File): Promise<string | ArrayBuffer> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (): void => {
            resolve(reader.result);
        };

        reader.onerror = reject;

        reader.readAsText(file);
    });
}

export function groupBy(xs, key) {
    return xs.reduce(function (rv, x) {
        (rv[x[key]] = rv[x[key]] || []).push(x);
        return rv;
    }, {});
};

export function convertFormatMoney(value: number) {
    return value.toLocaleString('en-US', {style: 'currency', currency: 'USD'});
}

export function removeFormatMoney(value: string) {
    return Number(value.replace(/[^\d.]/g, ''))
}

export function splitPeriodSUA(periodSUA) {
    const resultInit = periodSUA.split('-');
    let month = '';
    switch (resultInit[0]) {
        case 'Enero':
            month = '01';
            break;
        case 'Febrero':
            month = '02';
            break;
        case 'Marzo':
            month = '03';
            break;
        case 'Abril':
            month = '04';
            break;
        case 'Mayo':
            month = '05';
            break;
        case 'Junio':
            month = '06';
            break;
        case 'Julio':
            month = '07';
            break;
        case 'Agosto':
            month = '08';
            break;
        case 'Septiembre':
            month = '09';
            break;
        case 'Octubre':
            month = '10';
            break;
        case 'Noviembre':
            month = '11';
            break;
        case 'Diciembre':
            month = '12';
            break;
    }

    return month + '-' + resultInit[1].slice(2, 4);
}

export function gettext(data): Promise<any> {
    return pdfjsLib.getDocument(data).promise.then((pdf) => { // get all pages text
        // @ts-ignore
        const maxPages = pdf._pdfInfo.numPages;
        const countPromises = []; // collecting all page promises
        for (let j = 1; j <= maxPages; j++) {
            const page = pdf.getPage(j);

            const txt = '';
            countPromises.push(page.then((page) => { // add page promise
                const textContent = page.getTextContent();
                return textContent.then((text) => { // return content promise
                    return text.items.map((s) => {
                        // @ts-ignore
                        return s.str;
                    }).join(''); // value page text
                });
            }));
        }
        // Wait for all pages and join text
        return Promise.all(countPromises).then((texts) => {
            return texts.join('');
        });
    });
}

export async function extractXmlFilesRecursively(inputFile: File | Blob, jsZip: JSZip): Promise<Array<JSZip.JSZipObject>> {
    const xmlFiles: Array<JSZip.JSZipObject> = [];
    const zip = await jsZip.loadAsync(inputFile);

    // Function to process each file/folder recursively
    const processEntry = async (file: JSZip.JSZipObject): Promise<void> => {
        // Skip directories
        if (file.dir) return;

        const fileName: string = file.name.toLowerCase();

        // If it's an XML file, add it to results
        if (fileName.endsWith('.xml')) {
            xmlFiles.push(file);
            return;
        }

        // If it's a ZIP file, extract and process its contents
        if (fileName.endsWith('.zip')) {
            try {
                const content: Blob = await file.async('blob');
                const innerZip = new JSZip();
                const extractedZip = await innerZip.loadAsync(content);

                // Process all files in the inner ZIP
                for (const innerFile of Object.values(extractedZip.files))
                    await processEntry(innerFile);

            } catch (error) {
                console.error(`Error processing inner ZIP file ${file.name}:`, error);
            }
        }
    };

    // Process all files in the root ZIP
    for (const file of Object.values(zip.files))
        await processEntry(file);

    return xmlFiles;
}

export async function readXmlFile(file: JSZip.JSZipObject): Promise<string> {
    const arrayBuffer: ArrayBuffer = await file.async('arraybuffer');
    const u8: Uint8Array = new Uint8Array(arrayBuffer);
    if (u8.length === 0) return '';

    // 1) BOM detection
    if (u8.length >= 2) {
        if (u8[0] === 0xFF && u8[1] === 0xFE) {
            // UTF-16 LE BOM
            return new TextDecoder('utf-16le').decode(u8);
        }
        if (u8[0] === 0xFE && u8[1] === 0xFF) {
            // UTF-16 BE BOM
            return new TextDecoder('utf-16be').decode(u8);
        }
    }
    // UTF-8 BOM
    if (u8.length >= 3 && u8[0] === 0xEF && u8[1] === 0xBB && u8[2] === 0xBF) {
        return new TextDecoder('utf-8').decode(u8);
    }

    // 2) Null-byte pattern sniffing (very reliable for UTF-16 without BOM)
    const sample = Math.min(u8.length, 1000);
    let zerosEven = 0, zerosOdd = 0;
    for (let i = 0; i < sample; i++) {
        if (u8[i] === 0) {
            if ((i & 1) === 0) zerosEven++;
            else zerosOdd++;
        }
    }
    // If lots of zeros on odd indices => UTF-16LE (ASCII chars low byte then 0x00)
    if (zerosOdd > sample * 0.3 && zerosOdd > zerosEven) {
        try {
            return new TextDecoder('utf-16le').decode(u8);
        } catch (e) {
            console.error(e);
        }
    }
    // If lots of zeros on even indices => UTF-16BE
    if (zerosEven > sample * 0.3 && zerosEven > zerosOdd) {
        try {
            return new TextDecoder('utf-16be').decode(u8);
        } catch (e) {
            console.error(e);
        }
    }

    // 3) Try UTF-8 and look for encoding declaration
    const asUtf8 = new TextDecoder('utf-8').decode(u8);
    const m = asUtf8.match(/<\?xml[^>]*encoding=['"]([^'"]+)['"]/i);
    if (m && m[1]) {
        const declared = m[1].toLowerCase();
        // map 'utf-16' -> assume LE if no BOM (common on Windows); try both if needed
        const tryEncodings = declared === 'utf-16'
            ? ['utf-16le', 'utf-16be']
            : [declared, declared.replace(/[-_]/g, '')];

        for (const enc of tryEncodings) {
            try {
                return new TextDecoder(enc).decode(u8);
            } catch (e) {
                console.error(e);
            }
        }
    }

    // 4) Last resort: return UTF-8-decoded text
    return asUtf8;
}

export function formatPeriodToText(period: string): string {
    const months: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril',
        'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const periods: string[] = period.split('-');
    return months[Number(periods[0]) - 1] + ' 20' + periods[1];
}

/**
 * Converts a "yyyy-MM" period string to a readable Spanish month-year label.
 * Example: "2026-02" → "Febrero 2026"
 */
export function formatPeriodo(periodo: string): string {
    const months: string[] = ['Enero', 'Febrero', 'Marzo', 'Abril',
        'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const parts: string[] = periodo.split('-');
    if (parts.length !== 2) { return periodo; }
    const monthIndex = Number(parts[1]) - 1;
    return months[monthIndex] + ' ' + parts[0];
}

/**
 * Parses the JSON error body of an HTTP response that was requested with
 * `responseType: 'blob'` (the error arrives as a `Blob` in `error.error`
 * instead of a plain object).
 *
 * @param error the `HttpErrorResponse` from a blob request
 */
export async function parseBlobError(error: HttpErrorResponse): Promise<ErrorApiResponse> {
    try {
        const text = await (error.error as Blob).text();
        const parsed: ApiResponse<unknown> = JSON.parse(text);
        return parsed.error;
    } catch {
        return {error: 'Ha ocurrido un error inesperado, consulte con el administrador del sistema para más información.'};
    }
}

export function formatPeriodoShort(periodo: string): string {
    const months: string[] = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
        'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const parts: string[] = periodo.split('-');
    if (parts.length !== 2) { return periodo; }
    const monthIndex = Number(parts[1]) - 1;
    return months[monthIndex] + ' ' + parts[0].slice(-2);
}
